/**
 * ClientAcceptInvite — Phase 5.75-vocab Commit A (2026-04-26)
 *
 * Magic-link landing page for invitees: validates the token, prompts for
 * password setup if needed, redeems the invite, and lands the user in the
 * Client Portal.
 *
 * Path: /client/accept/:token  (mounted PUBLIC in App.jsx — invitees may
 *       not be signed in yet)
 *
 * Page states:
 *   1. validating  — peek_client_invite RPC running
 *   2. invalid     — token missing/malformed/expired/already-redeemed
 *   3. signup-form — valid token, user not signed in: show password setup
 *   4. signin-form — valid token, "user already exists" branch from signup
 *   5. wrong-user  — valid token, signed in as DIFFERENT email; offer sign-out
 *   6. submitting  — auth + redeem in flight
 *   7. success     — redirecting to /client/portal
 *
 * The peek RPC (anon-callable) returns:
 *   { reviewer_id, client_id, client_name, reviewer_email, reviewer_name,
 *     invite_sent_at, is_expired, is_redeemed }
 *
 * The redeem RPC (authenticated) verifies email-match server-side, sets
 * redeemed_by_individual_id, clears the access_token (single-use), creates
 * the individual row if missing, and returns { client_id, individual_id }.
 *
 * Tone: warm + professional. The recipient may be unfamiliar with Juuni —
 * lead with the Client they were invited to, not our brand. Calm guidance
 * through the steps; clear error messages on dead-end states.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { peekClientInvite, redeemClientInvite } from '../services/clientService';
import { COLORS } from '../constants/enums';
import Footer from '../components/Footer';

const STATES = {
  VALIDATING: 'validating',
  INVALID: 'invalid',
  SIGNUP_FORM: 'signup-form',
  SIGNIN_FORM: 'signin-form',
  WRONG_USER: 'wrong-user',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
};

export default function ClientAcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const [pageState, setPageState] = useState(STATES.VALIDATING);
  const [invite, setInvite] = useState(null);
  const [errorReason, setErrorReason] = useState(''); // for INVALID
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitError, setSubmitError] = useState('');

  // Step 1: Peek at the token to determine state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setErrorReason('No invitation token found in the link.');
        setPageState(STATES.INVALID);
        return;
      }
      const peeked = await peekClientInvite(token);
      if (cancelled) return;
      if (!peeked) {
        setErrorReason('This invitation link is not valid. It may have been mistyped or revoked.');
        setPageState(STATES.INVALID);
        return;
      }
      if (peeked.is_redeemed) {
        setErrorReason('This invitation has already been used. If you need access again, ask the team that invited you to send a fresh invite.');
        setPageState(STATES.INVALID);
        return;
      }
      if (peeked.is_expired) {
        setErrorReason('This invitation has expired. Ask the team that invited you to send a fresh invite — links are valid for 24 hours.');
        setPageState(STATES.INVALID);
        return;
      }
      setInvite(peeked);
      setName(peeked.reviewer_name || '');

      // Determine flow based on auth state.
      if (user?.email) {
        const userEmailNorm = user.email.trim().toLowerCase();
        const inviteEmailNorm = peeked.reviewer_email.trim().toLowerCase();
        if (userEmailNorm === inviteEmailNorm) {
          // Already signed in as the invited email — go straight to redeem.
          // (rare but possible: user signed up out-of-band before clicking the link)
          await tryRedeem();
          return;
        }
        // Signed in as a different account — block until sign-out.
        setPageState(STATES.WRONG_USER);
        return;
      }

      // Not signed in: show signup form.
      setPageState(STATES.SIGNUP_FORM);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function tryRedeem() {
    setPageState(STATES.SUBMITTING);
    const res = await redeemClientInvite(token);
    if (res?.success) {
      setPageState(STATES.SUCCESS);
      // Brief delay so the success message reads, then redirect.
      setTimeout(() => navigate('/client'), 800);
    } else {
      setSubmitError(res?.error || 'We could not redeem this invitation. Please try again or contact your team.');
      setPageState(STATES.SIGNUP_FORM);
    }
  }

  async function handleSignupSubmit(e) {
    e.preventDefault();
    setSubmitError('');
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match.');
      return;
    }
    setPageState(STATES.SUBMITTING);

    // Try signup first.
    // Phase 22.4 (Chapter 3 dogfood close 2026-04-30) — pass role: 'client'
    // so the post-signup bootstrap creates the public.individual row with
    // role='client' (instead of defaulting to 'annotator'). Without this,
    // the subsequent redeem_client_invite RPC throws
    //   "redeem_client_invite: existing user has role annotator;
    //    cannot redeem as client"
    // because the RPC refuses to auto-elevate from annotator to client
    // (mig 267 Layer 4 by-design — annotator → client elevation requires
    // explicit handoff). Surfaced by Halcyon Reviewer cold-start dogfood.
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: invite.reviewer_email,
      password,
      options: {
        data: { name: name || invite.reviewer_email, role: 'client' },
      },
    });

    if (signUpError) {
      // Common branch: "User already registered" → switch to sign-in form.
      const msg = (signUpError.message || '').toLowerCase();
      if (msg.includes('already registered') || msg.includes('already exists')) {
        setSubmitError('');
        setPageState(STATES.SIGNIN_FORM);
        return;
      }
      setSubmitError(signUpError.message || 'Could not create your account. Please try again.');
      setPageState(STATES.SIGNUP_FORM);
      return;
    }

    // Some Supabase setups require email confirmation before session is set.
    // If signUpData.session is null but no error, prompt the user to check
    // their email and try sign-in afterward.
    if (!signUpData?.session) {
      // Verify email-confirmation flow: most projects have it disabled for
      // SaaS, but be defensive. If session is null, try to sign in directly
      // (auto-confirms in dev).
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: invite.reviewer_email,
        password,
      });
      if (signInErr) {
        setSubmitError('Account created. Please check your email to confirm, then click the invite link again.');
        setPageState(STATES.SIGNUP_FORM);
        return;
      }
    }

    // Now signed in — redeem.
    await tryRedeem();
  }

  async function handleSigninSubmit(e) {
    e.preventDefault();
    setSubmitError('');
    setPageState(STATES.SUBMITTING);
    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email: invite.reviewer_email,
      password,
    });
    if (signInErr) {
      setSubmitError(signInErr.message || 'Could not sign in. Check your password.');
      setPageState(STATES.SIGNIN_FORM);
      return;
    }
    await tryRedeem();
  }

  async function handleSignOutAndRetry() {
    await signOut();
    // After sign-out, remount the page to re-evaluate flow. The simplest
    // way is a hard refresh of the same URL.
    window.location.reload();
  }

  // ── Render ──
  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--bg, #0a0a0b)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '24px 24px 0',
      fontFamily: 'var(--font-sans)',
    }}>
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}>
      <div style={{
        width: '100%',
        maxWidth: 440,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: 32,
        boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
      }}>
        {pageState === STATES.VALIDATING && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
              Validating invitation…
            </div>
          </div>
        )}

        {pageState === STATES.INVALID && (
          <>
            <h1 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 12px' }}>
              Invitation unavailable
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 24px' }}>
              {errorReason}
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                padding: '10px 20px', fontSize: 13, fontFamily: 'var(--font-mono)', fontWeight: 500,
                background: 'transparent', color: 'var(--text-muted)',
                border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
              }}
            >
              Go to sign in
            </button>
          </>
        )}

        {pageState === STATES.WRONG_USER && (
          <>
            <h1 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 12px' }}>
              Different account signed in
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 16px' }}>
              You're signed in as <strong>{user?.email}</strong>, but this invitation was sent to{' '}
              <strong>{invite?.reviewer_email}</strong>.
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 24px' }}>
              Sign out and click the invite link again to accept it as the invited address.
            </p>
            <button
              type="button"
              onClick={handleSignOutAndRetry}
              style={{
                padding: '10px 20px', fontSize: 13, fontFamily: 'var(--font-mono)', fontWeight: 500,
                background: COLORS.TEAL || '#5DCAA5', color: '#fff',
                border: 'none', borderRadius: 6, cursor: 'pointer',
              }}
            >
              Sign out and try again
            </button>
          </>
        )}

        {(pageState === STATES.SIGNUP_FORM || pageState === STATES.SUBMITTING) && invite && pageState !== STATES.SIGNIN_FORM && (
          <form onSubmit={handleSignupSubmit}>
            <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>
              Accept your invitation
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
              Set up your account to access <strong>{invite.client_name}</strong>'s Client Portal. You'll see project status, delivered batches, and quality reports.
            </p>

            <Field label="Email">
              <input
                type="email"
                value={invite.reviewer_email}
                readOnly
                aria-label="Invited email (cannot be changed)"
                style={{ ...inpStyle, opacity: 0.6, cursor: 'not-allowed' }}
              />
            </Field>

            <Field label="Your name (optional)">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={pageState === STATES.SUBMITTING}
                placeholder="e.g. Sara Smith"
                aria-label="Your name"
                style={inpStyle}
              />
            </Field>

            <Field label="Set a password" required>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={pageState === STATES.SUBMITTING}
                required
                minLength={8}
                placeholder="At least 8 characters"
                aria-label="Set a password"
                aria-required="true"
                autoComplete="new-password"
                style={inpStyle}
              />
            </Field>

            <Field label="Confirm password" required>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={pageState === STATES.SUBMITTING}
                required
                minLength={8}
                placeholder="Re-enter your password"
                aria-label="Confirm password"
                aria-required="true"
                autoComplete="new-password"
                style={inpStyle}
              />
            </Field>

            {submitError && (
              <div role="alert" style={{ padding: 10, marginBottom: 16, background: 'rgba(220, 38, 38, 0.08)', color: 'var(--text-error, #DC2626)', borderRadius: 6, fontSize: 12 }}>
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={pageState === STATES.SUBMITTING || password.length < 8 || password !== confirmPassword}
              style={{
                width: '100%', padding: '12px 20px', fontSize: 14, fontFamily: 'var(--font-mono)', fontWeight: 500,
                background: COLORS.TEAL || '#5DCAA5', color: '#fff',
                border: 'none', borderRadius: 6,
                cursor: (pageState === STATES.SUBMITTING || password.length < 8 || password !== confirmPassword) ? 'not-allowed' : 'pointer',
                opacity: (pageState === STATES.SUBMITTING || password.length < 8 || password !== confirmPassword) ? 0.6 : 1,
              }}
            >
              {pageState === STATES.SUBMITTING ? 'Setting up your account…' : 'Accept invitation'}
            </button>

            <p style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.5, margin: '16px 0 0', textAlign: 'center' }}>
              By accepting, you agree to Juuni AI's <a href="/legal/terms" style={{ color: 'var(--text-muted)', textDecoration: 'underline' }}>Terms</a> and <a href="/legal/privacy" style={{ color: 'var(--text-muted)', textDecoration: 'underline' }}>Privacy Policy</a>.
            </p>
          </form>
        )}

        {pageState === STATES.SIGNIN_FORM && invite && (
          <form onSubmit={handleSigninSubmit}>
            <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>
              Welcome back
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
              An account already exists for <strong>{invite.reviewer_email}</strong>. Sign in to accept your invitation to <strong>{invite.client_name}</strong>'s Client Portal.
            </p>

            <Field label="Email">
              <input
                type="email"
                value={invite.reviewer_email}
                readOnly
                aria-label="Email"
                style={{ ...inpStyle, opacity: 0.6, cursor: 'not-allowed' }}
              />
            </Field>

            <Field label="Password" required>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={pageState === STATES.SUBMITTING}
                required
                placeholder="Your password"
                aria-label="Password"
                aria-required="true"
                autoComplete="current-password"
                style={inpStyle}
              />
            </Field>

            {submitError && (
              <div role="alert" style={{ padding: 10, marginBottom: 16, background: 'rgba(220, 38, 38, 0.08)', color: 'var(--text-error, #DC2626)', borderRadius: 6, fontSize: 12 }}>
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={pageState === STATES.SUBMITTING || password.length === 0}
              style={{
                width: '100%', padding: '12px 20px', fontSize: 14, fontFamily: 'var(--font-mono)', fontWeight: 500,
                background: COLORS.TEAL || '#5DCAA5', color: '#fff',
                border: 'none', borderRadius: 6,
                cursor: (pageState === STATES.SUBMITTING || password.length === 0) ? 'not-allowed' : 'pointer',
                opacity: (pageState === STATES.SUBMITTING || password.length === 0) ? 0.6 : 1,
              }}
            >
              {pageState === STATES.SUBMITTING ? 'Signing in…' : 'Sign in & accept'}
            </button>
          </form>
        )}

        {pageState === STATES.SUCCESS && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <h1 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>
              You're all set
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>
              Taking you to your portal…
            </p>
          </div>
        )}
      </div>
      </div>
      <div style={{ width: '100%', maxWidth: 1080 }}>
        <Footer variant="auth" />
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{
        display: 'block', fontSize: 12, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
      }}>
        {label}{required && <span style={{ color: 'var(--text-error, #DC2626)', marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const inpStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 13,
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  outline: 'none',
  boxSizing: 'border-box',
};
